import { createReducer, on } from '@ngrx/store';
import { IReservationDetail, IReservationItem, OutlookReservationMode } from '@outlook-addin/cue-http';
import { ReservationActions } from '../actions';

export type State = {
  reservationOutlookMode: OutlookReservationMode;
  reservationList: IReservationItem[];
  reservationDetail: IReservationDetail;
  reservationError: string | null;
  eventDetail?: any;
  isLoading: boolean;
  isReservationLocationChangeActive: boolean;
  isReservationLocationChangeReturning: boolean;
  hiddenReservationDetailResourceIds: number[];
  dismissedResourceRemovalConfirmationResourceIds: number[];
};

export const initialState: State = {
  reservationOutlookMode: OutlookReservationMode.NotReservationDetail,
  reservationList: [],
  reservationDetail: null,
  reservationError: null,
  isLoading: false,
  isReservationLocationChangeActive: false,
  isReservationLocationChangeReturning: false,
  hiddenReservationDetailResourceIds: [],
  dismissedResourceRemovalConfirmationResourceIds: [],
};

const getEventDetailKey = (eventDetail: unknown): string | null => {
  if (!eventDetail || typeof eventDetail !== 'object') {
    return null;
  }

  const detail = eventDetail as Record<string, unknown>;
  const key = [
    detail['id'],
    detail['iCalUId'],
    detail['iCalUid'],
    detail['icalUid'],
    detail['ICalUid'],
    detail['uid'],
    detail['exchangeUniqueId'],
    detail['exchangeId'],
  ].find((value) => typeof value === 'string' && value.trim().length > 0);

  return typeof key === 'string' ? key.trim() : null;
};

const isSameEventDetail = (currentEventDetail: unknown, nextEventDetail: unknown): boolean => {
  const currentKey = getEventDetailKey(currentEventDetail);
  const nextKey = getEventDetailKey(nextEventDetail);

  if (currentKey && nextKey) {
    return currentKey === nextKey;
  }

  return currentEventDetail === nextEventDetail;
};

export const reducer = createReducer(
  initialState,
  on(ReservationActions.loadReservationList, (state) => ({
    ...state,
    reservationList: [],
    isLoading: true,
  })),
  on(ReservationActions.reservationListLoaded, (state, action) => ({
    ...state,
    reservationList: action.reservationList,
    reservationDetail: null,
    isLoading: false,
  })),
  on(ReservationActions.loadReservationDetail, (state, { eventDetail, preserveCurrentDetail }) => {
    const isSameEvent = isSameEventDetail(state.eventDetail, eventDetail);

    return {
      ...state,
      eventDetail: eventDetail,
      reservationDetail: preserveCurrentDetail && isSameEvent ? state.reservationDetail : null,
      hiddenReservationDetailResourceIds:
        preserveCurrentDetail && isSameEvent ? state.hiddenReservationDetailResourceIds : [],
      dismissedResourceRemovalConfirmationResourceIds:
        preserveCurrentDetail && isSameEvent ? state.dismissedResourceRemovalConfirmationResourceIds : [],
    };
  }),
  on(ReservationActions.refreshReservationDetail, (state) => ({
    ...state,
  })),
  on(ReservationActions.reservationDetailLoadSuccess, (state, action) => ({
    ...state,
    reservationDetail: action.detail,
    reservationError: null,
    isReservationLocationChangeActive: action.preserveLocationChangeState
      ? state.isReservationLocationChangeActive
      : false,
    isReservationLocationChangeReturning: action.preserveLocationChangeState
      ? state.isReservationLocationChangeReturning
      : false,
  })),
  on(ReservationActions.reservationDetailLoadFailed, (state, action) => ({
    ...state,
    reservationDetail: null,
    reservationError: action.error,
    isReservationLocationChangeActive: action.preserveLocationChangeState
      ? state.isReservationLocationChangeActive
      : false,
    isReservationLocationChangeReturning: action.preserveLocationChangeState
      ? state.isReservationLocationChangeReturning
      : false,
    hiddenReservationDetailResourceIds: [],
    dismissedResourceRemovalConfirmationResourceIds: [],
  })),
  on(ReservationActions.hideReservationDetailResources, (state, action) => ({
    ...state,
    hiddenReservationDetailResourceIds: Array.from(
      new Set([...state.hiddenReservationDetailResourceIds, ...action.resourceIds])
    ),
  })),
  on(ReservationActions.showReservationDetailResources, (state, action) => {
    const resourceIdsToShow = new Set(action.resourceIds);

    return {
      ...state,
      hiddenReservationDetailResourceIds: state.hiddenReservationDetailResourceIds.filter(
        (resourceId) => !resourceIdsToShow.has(resourceId)
      ),
    };
  }),
  on(ReservationActions.dismissResourceRemovalConfirmation, (state, action) => ({
    ...state,
    dismissedResourceRemovalConfirmationResourceIds: Array.from(
      new Set([...state.dismissedResourceRemovalConfirmationResourceIds, action.resourceId])
    ),
  })),
  on(ReservationActions.clearDismissedResourceRemovalConfirmations, (state) => ({
    ...state,
    dismissedResourceRemovalConfirmationResourceIds: [],
  })),
  on(ReservationActions.startReservationLocationChange, (state) => ({
    ...state,
    isReservationLocationChangeActive: true,
    isReservationLocationChangeReturning: false,
  })),
  on(ReservationActions.cancelReservationLocationChange, (state) => ({
    ...state,
    isReservationLocationChangeActive: false,
    isReservationLocationChangeReturning: false,
  })),
  on(ReservationActions.returnFromReservationLocationChange, (state) => ({
    ...state,
    isReservationLocationChangeReturning: true,
  })),
  on(ReservationActions.outlookReservationModeChange, (state, action) => ({
    ...state,
    reservationOutlookMode: action.reservationMode,
    isReservationLocationChangeActive:
      action.reservationMode === OutlookReservationMode.NotReservationDetail
        ? state.isReservationLocationChangeActive
        : false,
    isReservationLocationChangeReturning:
      action.reservationMode === OutlookReservationMode.NotReservationDetail
        ? state.isReservationLocationChangeReturning
        : false,
  }))
);
